import { useState } from 'react'
import { loadWhop, type Appearance } from '@whop/elements'
import { WhopElements, Checkout, CheckoutElement } from '@whop/elements-react'
import { X, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { PINK } from '../lib/theme'


/**
 * Everything the embedded Whop checkout needs, as returned by
 * POST /api/whop/checkout. The worker mints a checkout configuration (carrying
 * our brand_id/tier/mode metadata) and hands back its id, so rendering it
 * in-app attributes the purchase to the brand exactly like the old hosted
 * redirect did.
 */
export interface WhopCheckoutParams {
  /**
   * The plan being purchased. Informational only: the checkout configuration
   * already names its plan (or none, for a `mode: 'setup'` card-save session),
   * and Whop Elements refuses a plan that differs from the configuration's.
   */
  planId?: string
  /** The checkout configuration id (`ch_…`) — carries our metadata to webhooks. */
  sessionId: string
  /** Brand email to prefill. When present we also lock the field. */
  email?: string | null
  /** Must match the Whop env the configuration was minted against (prod vs sandbox). */
  environment?: 'production' | 'sandbox'
  /** Where to land after payment — re-enters CheckoutActivation's sync poll. */
  returnUrl: string
}

// The dashboard is dark-only, so the checkout is pinned to dark with our pink
// accent (the legacy embed's 'system' theme could render a light form inside
// the dark modal). Module-level so re-renders never hand Elements a "new" value.
//
// The "Welcome back. Sign in to use your saved payment methods" control is
// hidden: it signs the buyer into their personal Whop account, which the legacy
// embed never offered. The card we charge for hires is the one this checkout
// saves to the brand (via the webhook), so that sign-in adds nothing here.
//
// The billing country/state selects get `color-scheme: dark`: they are native
// <select>s, and without it the browser draws the open option list light with
// the theme's white text, so the countries are unreadable. The explicit dark
// background + light text are the fallback if Whop's style filter drops
// color-scheme (browsers draw the option list in the select's colors). Style only.
// The item line (the Whop product's name, e.g. "Creator Roster Wallet Top-up")
// is hidden too: it crowds the header, and every caller already says what is
// being bought before the checkout opens. The price stays.
const APPEARANCE: Appearance = {
  theme: { appearance: 'dark', accentColor: 'pink' },
  classes: {
    'whop-EmailSignIn': { display: 'none' },
    'whop-EmailSignInError': { display: 'none' },
    'whop-CheckoutItem': { display: 'none' },
    'whop-AddressFieldSelect': { colorScheme: 'dark', backgroundColor: '#1c1c1c', color: '#ededed' },
  },
}

/**
 * In-app Whop checkout, built on Whop Elements (the legacy embedded checkout,
 * @whop/checkout, stops working 2026-10-21). The buyer never leaves the
 * dashboard; on success we navigate to returnUrl (?from=checkout), which
 * CheckoutActivation picks up to poll the subscription live.
 *
 * Elements opens the checkout session itself from our checkout configuration,
 * so the plan, metadata (brand_id / account_id / tier / mode / topup_id),
 * setup mode and redirect all come from the same object the worker always
 * created — webhooks, sync and recurring billing are untouched.
 *
 * The email is only locked when we actually have one to prefill — locking an
 * empty field would trap the buyer with no way to enter their address.
 */
export default function WhopCheckoutModal({
  params,
  onClose,
  onComplete,
}: {
  params: WhopCheckoutParams
  onClose: () => void
  onComplete: () => void
}) {
  // One load per modal mount. loadWhop() shares a single script + promise
  // across calls, so reopening the modal doesn't re-download the SDK.
  const [sdk, setSdk] = useState(() => loadWhop())
  const [failed, setFailed] = useState<null | 'sdk' | 'checkout'>(null)

  const retry = () => {
    // A failed SDK load is held by loadWhop(); retry() starts a fresh one.
    if (failed === 'sdk') setSdk(prev => prev.retry())
    setFailed(null)
  }

  const email = params.email || undefined

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 20,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-primary)',
          borderRadius: 14,
          width: '100%',
          maxWidth: 520,
          marginTop: '4vh',
          marginBottom: '4vh',
          padding: 20,
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close checkout"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            zIndex: 1,
          }}
        >
          <X size={20} />
        </button>

        {failed ? (
          <CheckoutError onRetry={retry} />
        ) : (
          <WhopElements
            elements={sdk}
            environment={params.environment}
            appearance={APPEARANCE}
            onLoadError={() => setFailed('sdk')}
          >
            {/* Every Checkout option is fixed at creation; keying on the
                configuration id remounts (never mutates) if it ever changes. */}
            <Checkout
              key={params.sessionId}
              checkoutConfiguration={params.sessionId}
              returnUrl={params.returnUrl}
              onComplete={() => onComplete()}
              fallback={<CheckoutLoading />}
            >
              <CheckoutElement
                buyerEmail={email}
                lockBuyerEmail={!!email}
                onError={e => {
                  console.error('[WhopCheckoutModal] checkout element failed:', e.code ?? '', e.message)
                  setFailed('checkout')
                }}
              />
            </Checkout>
          </WhopElements>
        )}
      </div>
    </div>
  )
}

function CheckoutLoading() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        minHeight: 340,
        color: 'var(--text-secondary)',
        fontSize: 14,
      }}
    >
      <Loader2 size={22} className="animate-spin" style={{ color: PINK }} />
      Loading secure checkout…
    </div>
  )
}

function CheckoutError({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        minHeight: 340,
        textAlign: 'center',
        padding: '0 12px',
      }}
    >
      <AlertCircle size={28} style={{ color: PINK }} />
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
        Checkout couldn't load
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 340 }}>
        Nothing was charged. Check your connection (or any ad blocker) and try again.
      </div>
      <button
        onClick={onRetry}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 4,
          padding: '10px 20px',
          background: PINK,
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        <RefreshCw size={14} />
        Try again
      </button>
    </div>
  )
}
